import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  PhoneCall,
  ShieldAlert,
  Navigation,
  CheckCircle2,
  XCircle,
  Car,
  Clock,
  KeyRound,
  ListOrdered,
  Share2,
  Map 
} from "lucide-react";
import api, { DRIVER_IMAGE_URL } from "../api/Api";
import { io } from "socket.io-client";

export default function ActiveTrip({ tripId }) {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const id = tripId || paramId;

  // --- DYNAMIC STATE ---
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tripState, setTripState] = useState("CONFIRMATION_PENDING");
  const [tripDetails, setTripDetails] = useState(null);

  // Interactive States
  const [shareLiveLocation, setShareLiveLocation] = useState(false);
  const [customerResponse, setCustomerResponse] = useState(null);

  // --- 1. FETCH DATA ---
  const fetchActiveTripDetails = async () => {
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("customerToken");

      const response = await api.get(`/bookingRoutes/details/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data.data || response.data;

      if (!data) {
        setError("Could not retrieve trip data.");
        setLoading(false);
        return;
      }

      setTripDetails({
        bookingId: data.enqref_no,
        enq_id: data.enq_id,
        driverName: data.driver_name,
        driverPhone: data.driver_phone,
        carModel: data.taxi_car_name || "Assigned Cab",
        pickup: data.pickup_location,
        destinations: data.dropoff_location,
        vehicleNo: data.vehicle_no || null,
        
        startOtp: data.cust_otp || null, 
        otpVerified: data.otp_verified || 0,
        
        endOtp: data.end_otp || null,
        endOdo: data.end_odo || null,
        isMultiDay: data.is_multiday === 1,
        currentDay: data.current_day,
        totalDays: data.total_days,
      });

      setCustomerResponse(data.customer_avail_response || null);
      setShareLiveLocation(data.is_sharing_location === 1);

      determineTripState(data);
    } catch (err) {
      console.error("Error fetching trip details:", err);
      setError(err.response?.data?.message || "Failed to load active trip details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      fetchActiveTripDetails();
    } else {
      setError("No trip ID provided.");
      setLoading(false);
    }
  }, [id]);

  const determineTripState = (data) => {
    if (data.otp_verified === 1) {
      setTripState(data.is_ready_to_end === 1 ? "READY_TO_END" : "IN_PROGRESS");
    } else if (data.customer_avail_response === "YES" && data.cust_otp) {
      setTripState("READY_TO_START");
    } else {
      setTripState("CONFIRMATION_PENDING");
    }
  };

  const handleAvailTrip = async (avail) => {
    const responseValue = avail ? "YES" : "NO";
    setCustomerResponse(responseValue);

    try {
      await api.post(`/bookingRoutes/availResponse`, {
        bookingId: id,
        response: responseValue,
      });
      if (avail) fetchActiveTripDetails();
    } catch (err) {
      alert("Action failed");
    }
  };

  const toggleLocationShare = async () => {
    const newStatus = !shareLiveLocation;
    setShareLiveLocation(newStatus);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("customerToken");
      await api.post(
        `/bookingRoutes/toggleLocation`,
        { bookingId: id, isSharing: newStatus ? 1 : 0 },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (err) {
      alert("Could not update location preferences.");
      setShareLiveLocation(!newStatus);
    }
  };

  // =========================================================================
  // LIVE LOCATION TRACKER
  // =========================================================================
  useEffect(() => {
    let watchId;
    let driverSocket;

    const isTripActive = tripState === "IN_PROGRESS" || tripState === "READY_TO_END";

    if (shareLiveLocation && tripDetails?.bookingId && !isTripActive) {
      driverSocket = io(DRIVER_IMAGE_URL, { transports: ["websocket"] });

      driverSocket.on("connect", () => {
        driverSocket.emit("join_booking_room", tripDetails.bookingId);
      });

      driverSocket.on("driver_arrived_otp_sent", () => {
         fetchActiveTripDetails(); 
      });

      driverSocket.on("trip_started", () => {
        fetchActiveTripDetails();
      });

      if ("geolocation" in navigator) {
        watchId = navigator.geolocation.watchPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            driverSocket.emit("customer_live_location", {
              bookingId: tripDetails.bookingId,
              lat: latitude,
              lng: longitude,
            });
          },
          (error) => console.error("Error watching location:", error),
          { enableHighAccuracy: true, maximumAge: 0 }
        );
      }
    }

    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
      if (driverSocket) driverSocket.disconnect();
    };
  }, [shareLiveLocation, tripDetails, tripState]); 

  // --- SHARED UI COMPONENT: ITINERARY ---
  const renderItinerary = () => (
    <div className="bg-white rounded-4 shadow-sm border border-light overflow-hidden mb-3">
      <div className="p-3 border-bottom border-light bg-light d-flex justify-content-between align-items-center">
        <h6 className="fw-bold text-dark m-0 text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
          <ListOrdered size={14} className="me-1 mb-1 text-primary" /> Trip Itinerary
        </h6>
      </div>
      <div className="p-4">
        <div className="position-relative ms-1">
          <div className="position-absolute timeline-connect-line" style={{ top: "12px", bottom: "16px", left: "5px" }}></div>
          
          <div className="d-flex position-relative mb-4">
            <div className="bg-white border border-3 border-success rounded-circle z-index-1 mt-1 me-3 flex-shrink-0" style={{ width: "14px", height: "14px" }}></div>
            <div>
              <small className="text-muted d-block fw-bold mb-1" style={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}>PICKUP</small>
              <div className="fw-bold text-dark" style={{ fontSize: "0.9rem" }}>{tripDetails.pickup || "Not Specified"}</div>
            </div>
          </div>

          <div className="d-flex position-relative mb-2">
            <div className="bg-white border border-3 border-danger rounded-circle z-index-1 mt-1 me-3 flex-shrink-0" style={{ width: "14px", height: "14px" }}></div>
            <div>
              <small className="text-muted d-block fw-bold mb-1" style={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}>DROP-OFF</small>
              <div className="fw-bold text-dark" style={{ fontSize: "0.9rem" }}>{tripDetails.destinations || "Not Specified"}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // --- UI RENDERERS ---
  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center min-vh-100 bg-white">
        <div className="spinner-border text-dark mb-3" style={{ borderWidth: "3px" }} role="status"></div>
        <span className="text-dark fw-bold small">Connecting to your ride...</span>
      </div>
    );
  }

  if (error || !tripDetails) {
    return (
      <div className="bg-light min-vh-100 p-3 pt-5 d-flex flex-column align-items-center justify-content-center">
        <div className="bg-white p-5 rounded-4 shadow-sm border border-light text-center w-100" style={{ maxWidth: "400px" }}>
          <div className="bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex p-3 mb-3"><XCircle size={32} /></div>
          <h5 className="fw-bold text-dark mb-2">Trip Not Found</h5>
          <p className="text-muted small mb-4">{error || "We couldn't find the details for this trip."}</p>
          <button className="btn btn-dark w-100 rounded-pill fw-bold py-3 btn-native-press" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  // 🟢 PHASE 1: Waiting for Driver
  const renderConfirmation = () => (
    <div className="animate-slide-up">
      <div className="bg-white rounded-4 shadow-sm border border-light overflow-hidden mb-3">
        <div className="bg-warning bg-opacity-10 p-3 d-flex align-items-center gap-3 border-bottom border-warning border-opacity-25">
          <div className="bg-white text-warning rounded-circle p-2 shadow-sm flex-shrink-0"><Clock size={20} /></div>
          <div>
            <h6 className="fw-bold text-dark m-0 mb-1" style={{ fontSize: "0.9rem" }}>Ride Starts Soon</h6>
            <p className="small text-muted m-0 lh-sm" style={{ fontSize: "0.75rem" }}>Please confirm your availability to proceed.</p>
          </div>
        </div>
        <div className="p-4">
          {customerResponse === null ? (
            <div className="d-flex flex-column gap-2 mb-4">
              <button onClick={() => handleAvailTrip(true)} className="btn btn-dark w-100 py-3 fw-bold rounded-pill d-flex align-items-center justify-content-center gap-2 btn-native-press shadow-sm">
                <CheckCircle2 size={18} /> Yes, I will take this ride
              </button>
              <button onClick={() => handleAvailTrip(false)} className="btn btn-light border w-100 py-3 fw-bold rounded-pill text-danger d-flex align-items-center justify-content-center gap-2 btn-native-press">
                <XCircle size={18} /> No, cancel ride
              </button>
            </div>
          ) : (
            <>
              <div className="bg-success bg-opacity-10 border border-success border-opacity-25 rounded-4 p-3 mb-3 d-flex align-items-center gap-3">
                <CheckCircle2 size={24} className="text-success flex-shrink-0" />
                <div>
                  <h6 className="fw-bold text-success m-0" style={{ fontSize: "0.85rem" }}>Trip Confirmed</h6>
                  <span className="text-muted small" style={{ fontSize: "0.75rem" }}>Waiting for driver to arrive...</span>
                </div>
              </div>

              <div className="bg-light p-3 rounded-4 d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-white p-2 rounded-circle shadow-sm"><Share2 size={16} className="text-primary" /></div>
                  <div>
                    <h6 className="fw-bold text-dark m-0" style={{ fontSize: "0.85rem" }}>Share Live Location</h6>
                    <span className="text-muted" style={{ fontSize: "0.7rem" }}>Help the driver find you</span>
                  </div>
                </div>
                <div className="form-check form-switch m-0">
                  <input className="form-check-input" type="checkbox" role="switch" style={{ width: "40px", height: "20px", cursor: "pointer" }} checked={shareLiveLocation} onChange={toggleLocationShare} />
                </div>
              </div>

              {/* 🟢 NEW BUTTON: TRACK DRIVER ON MAP */}
              <button
                onClick={() => navigate(`/live-track-ride/${tripDetails.enq_id}`, { state: { tripDetails, tripState } })}
                className="btn btn-primary w-100 rounded-pill py-3 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2 btn-native-press mb-2"
                style={{ background: "linear-gradient(135deg, #0d6efd, #0a58ca)", border: "none", fontSize: "0.95rem" }}
              >
                <Map size={20} />
                TRACK DRIVER ON MAP
              </button>
            </>
          )}
        </div>
      </div>
      {renderItinerary()}
    </div>
  );

  // 🟢 PHASE 1.5: Driver Arrived, showing OTP
  const renderReadyToStart = () => {
    const otpDigits = (tripDetails.startOtp ? String(tripDetails.startOtp) : "----").split("");
    return (
      <div className="animate-slide-up">
        <div className="bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded-4 p-3 mb-3 d-flex justify-content-between align-items-center">
          <div>
            <h6 className="fw-bold text-primary m-0 mb-1" style={{ fontSize: "0.85rem" }}><KeyRound size={14} className="me-1 mb-1" /> Start OTP</h6>
            <span className="text-primary opacity-75 fw-medium" style={{ fontSize: "0.7rem" }}>Share to start ride</span>
          </div>
          <div className="d-flex gap-1">
            {otpDigits.map((digit, i) => (
              <div key={i} className="bg-white border border-primary border-opacity-50 text-primary fw-bold rounded-3 d-flex align-items-center justify-content-center shadow-sm" style={{ width: "32px", height: "40px", fontSize: "1.2rem" }}>
                {digit}
              </div>
            ))}
          </div>
        </div>

        {/* 🟢 TRACK DRIVER BUTTON - Still available if driver is waiting outside */}
        <button
          onClick={() => navigate(`/live-track-ride/${tripDetails.enq_id}`, { state: { tripDetails, tripState } })}
          className="btn btn-primary w-100 rounded-pill py-3 mb-4 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2 btn-native-press"
          style={{ background: "linear-gradient(135deg, #0d6efd, #0a58ca)", border: "none", fontSize: "0.95rem" }}
        >
          <Map size={20} />
          TRACK DRIVER ON MAP
        </button>

        {renderItinerary()}
      </div>
    );
  };

  // 🟢 PHASE 2: Trip in Progress
  const renderInProgress = () => (
    <div className="animate-slide-up">
      {/* 🟢 REPLACED THE "RIDE IN PROGRESS" TEXT WITH THIS BIG BUTTON */}
      <button
        onClick={() => navigate(`/live-track-ride/${tripDetails.enq_id}`, { state: { tripDetails, tripState } })}
        className="btn btn-success w-100 rounded-pill py-3 mb-4 fw-bold shadow-lg d-flex align-items-center justify-content-center gap-2 btn-native-press"
        style={{ fontSize: "1rem", letterSpacing: "0.5px" }}
      >
        <Map size={22} />
        VIEW RIDE ON MAP
      </button>

      {tripDetails.isMultiDay && (
        <div className="text-center mb-3">
          <span className="badge bg-success text-white rounded-pill fw-bold px-3 py-2" style={{ fontSize: "0.8rem" }}>
            Day {tripDetails.currentDay} of {tripDetails.totalDays}
          </span>
        </div>
      )}

      {renderItinerary()}
    </div>
  );

  // 🟢 PHASE 3: Trip Ended
  const renderReadyToEnd = () => {
    const otpDigits = (tripDetails.endOtp ? String(tripDetails.endOtp) : "----").split("");
    return (
      <div className="animate-slide-up">
        <div className="bg-dark text-white rounded-4 p-3 mb-3 d-flex justify-content-between align-items-center shadow-sm">
          <div>
            <h6 className="fw-bold m-0 mb-1" style={{ fontSize: "0.85rem" }}>
              <CheckCircle2 size={14} className="me-1 mb-1" /> End PIN
            </h6>
            <span className="opacity-75 fw-medium" style={{ fontSize: "0.7rem" }}>Share to complete</span>
          </div>
          <div className="d-flex gap-1">
            {otpDigits.map((digit, i) => (
              <div key={i} className="bg-white text-dark fw-bold rounded-3 d-flex align-items-center justify-content-center shadow-sm" style={{ width: "32px", height: "40px", fontSize: "1.2rem" }}>
                {digit}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-4 shadow-sm border border-light p-3 mb-3 d-flex justify-content-between align-items-center">
          <div>
            <span className="text-muted fw-bold d-block mb-1" style={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}>FINAL ODOMETER</span>
            <span className="fw-bold text-dark fs-6">{tripDetails.endOdo || "0"} KM</span>
          </div>
          <span className="badge bg-warning text-dark border">Verify Meter</span>
        </div>

        {renderItinerary()}
      </div>
    );
  };

  return (
    <div className="bg-light min-vh-100 font-sans d-flex flex-column position-relative">
      <div className="position-relative w-100 bg-secondary flex-shrink-0" style={{ height: "25vh", minHeight: "200px" }}>
        <div className="w-100 h-100 bg-light">
          {tripDetails ? (
            <iframe
              title="trip-route-map" width="100%" height="100%"
              style={{ border: 0, pointerEvents: "none", filter: "brightness(0.95) contrast(1.05)" }}
              loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade"
              src={
                tripDetails.destinations[0] && tripDetails.destinations[0] !== "Destination" && tripDetails.destinations[0] !== "Not Specified"
                  ? `https://maps.google.com/maps?saddr=${encodeURIComponent(tripDetails.pickup)}&daddr=${encodeURIComponent(tripDetails.destinations[0])}&output=embed`
                  : `https://maps.google.com/maps?q=${encodeURIComponent(tripDetails.pickup)}&output=embed`
              }
            ></iframe>
          ) : (
            <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center" style={{ backgroundColor: "#e2e8f0", backgroundImage: "radial-gradient(#cbd5e1 1px, transparent 1px)", backgroundSize: "20px 20px" }}>
              <MapPin size={48} className="text-primary opacity-50 mb-2" />
            </div>
          )}
        </div>

        <div className="position-absolute top-0 w-100 px-3 py-4 d-flex justify-content-between align-items-center z-index-1000" style={{ background: "linear-gradient(to bottom, rgba(255,255,255,0.9), transparent)" }}>
          <div className="bg-white rounded-circle p-2 shadow-sm cursor-pointer btn-native-press" onClick={() => navigate(-1)}><ArrowLeft className="text-dark" size={20} /></div>
          <div className="bg-white px-3 py-1 rounded-pill shadow-sm d-flex align-items-center gap-2 border border-light">
            <div className={`rounded-circle ${tripState === "IN_PROGRESS" ? "bg-success" : "bg-warning"}`} style={{ width: "8px", height: "8px" }}></div>
            <span className="fw-bold text-dark font-monospace" style={{ fontSize: "0.75rem" }}>{tripDetails?.bookingId || "Loading..."}</span>
          </div>
          <div className="bg-white rounded-circle p-2 shadow-sm cursor-pointer btn-native-press"><ShieldAlert className="text-danger" size={20} /></div>
        </div>
      </div>

      <div className="flex-grow-1 bg-light rounded-top-4 position-relative z-index-1 px-3 pt-4 pb-5" style={{ marginTop: "-20px", boxShadow: "0 -10px 30px rgba(0,0,0,0.05)" }}>
        <div className="mx-auto" style={{ maxWidth: "600px" }}>
          
          <div className="bg-white rounded-4 shadow-sm p-3 mb-4 d-flex align-items-center justify-content-between border border-light">
            <div className="d-flex align-items-center gap-3 overflow-hidden">
              <div className="bg-light rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 border border-light" style={{ width: "45px", height: "45px" }}><Car size={22} className="text-dark" /></div>
              <div className="overflow-hidden pe-2">
                <h6 className="fw-bold m-0 text-dark text-truncate" style={{ fontSize: "0.95rem" }}>{tripDetails.driverName || "Driver Assigning"}</h6>
                <span className="text-muted d-block text-truncate mt-1 fw-medium" style={{ fontSize: "0.75rem" }}>{tripDetails.vehicleNo} • {tripDetails.carModel}</span>
              </div>
            </div>
            {tripDetails.driverPhone && (
              <a href={`tel:${tripDetails.driverPhone}`} className="bg-success bg-opacity-10 text-success rounded-circle p-3 btn-native-press text-decoration-none flex-shrink-0 d-flex align-items-center justify-content-center shadow-sm">
                <PhoneCall size={18} />
              </a>
            )}
          </div>

          {tripState === "CONFIRMATION_PENDING" && renderConfirmation()}
          {tripState === "READY_TO_START" && renderReadyToStart()}
          {tripState === "IN_PROGRESS" && renderInProgress()}
          {tripState === "READY_TO_END" && renderReadyToEnd()}
        </div>
      </div>

      <style>{`
        .z-index-1000 { z-index: 1000; }
        .z-index-1 { z-index: 1; }
        .cursor-pointer { cursor: pointer; }
        .btn-native-press { transition: transform 0.1s ease, opacity 0.1s ease; }
        .btn-native-press:active { transform: scale(0.96); opacity: 0.8; }
        .timeline-connect-line { width: 2px; background-image: linear-gradient(to bottom, #ced4da 50%, transparent 50%); background-size: 2px 8px; background-repeat: repeat-y; z-index: 0; }
        @keyframes slideUpFade { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-slide-up { animation: slideUpFade 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes pulse-spin { 0% { transform: scale(1) rotate(0deg); } 50% { transform: scale(1.2) rotate(180deg); } 100% { transform: scale(1) rotate(360deg); } }
        .spin-slow { animation: pulse-spin 3s infinite linear; }
      `}</style>
    </div>
  );
}